/**
 * Phase 107 (Plan 107-08 / D-04, D-05, D-09, T-107-TEAMRESURRECT) — the
 * band list sidebar card.
 *
 * Ports the recovered `CoverageTeamSidebar.tsx` (git show bec322bc)'s Teams
 * card and drops its entire member-management sub-tree wholesale: no
 * `TeamMembersExpand`, no add/remove-member row, no per-member role picker,
 * no `projectId`/`eligibleUsers` props. D-05 is explicit that no team or
 * member concept exists in this phase at all — a price band is a named,
 * coloured label carrying an hourly rate, and it is the only paintable
 * label (D-04).
 *
 * The recovered sidebar's weeks-per-month monthly-factor constant (around
 * line 75) and its aggregation loop (around lines 355-381) are **not**
 * ported — that arithmetic now lives once, in `priceMath.ts` (Plan 107-07),
 * so this component never computes money.
 *
 * The rate is inline-editable directly in the row (no popover — D-04 moved
 * the rate off the cell and onto the band, so there is no more per-cell
 * rate to edit). The comma-decimal normalisation and the exact validation
 * error copy are ported verbatim from the deleted `CellRateEditorPopover.tsx`
 * (git show bec322bc) — this already fixed a real 2026-05-26 UAT complaint
 * (Czech keyboards emit a comma for the decimal separator) and must not be
 * re-derived from scratch.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { AddBandModal } from './AddBandModal';
import { DeleteBandConfirmModal } from './DeleteBandConfirmModal';
import type { SlaPricingBand } from '../../services/api/slaPricing';

// Ported verbatim from `CellRateEditorPopover.tsx` (git show bec322bc) —
// Petra UAT 2026-05-26: Czech keyboards emit a comma for the decimal
// separator by default. Normalize to `.` before validation/send so the
// backend keeps receiving canonical dot-decimal strings.
function normalizeDecimal(input: string): string {
  return input.replace(',', '.');
}

// Ported verbatim from `CellRateEditorPopover.tsx`'s `validate` regex chain
// and exact error copy, minus the empty-string branch — an empty rate here
// means "clear the rate" (D-09: band with no rate is a valid, visible
// state), not an error to report.
function validateRate(input: string): string | null {
  const normalized = normalizeDecimal(input);
  if (!/^[0-9]+(\.[0-9]{0,2})?$/.test(normalized)) {
    if (/\..*\..*/.test(normalized)) return 'Enter a numeric rate (e.g. 120 or 120.50).';
    if (/\.[0-9]{3,}/.test(normalized)) return 'Rate supports up to 2 decimal places.';
    if (/^-/.test(normalized)) return 'Rate must be 0 or greater.';
    return 'Enter a numeric rate (e.g. 120 or 120.50).';
  }
  const n = Number(normalized);
  if (!Number.isFinite(n)) return 'Enter a numeric rate (e.g. 120 or 120.50).';
  if (n < 0) return 'Rate must be 0 or greater.';
  if (n > 99999.99) return 'Rate must be 99999.99 or less.';
  return null;
}

interface RateEditorProps {
  band: SlaPricingBand;
  onUpdateRate: (bandId: number, hourlyRate: string | null) => void;
}

function RateEditor({ band, onUpdateRate }: RateEditorProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(band.hourlyRate ?? '');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  function startEdit(e: React.MouseEvent) {
    e.stopPropagation();
    setValue(band.hourlyRate ?? '');
    setError(null);
    setEditing(true);
  }

  const commit = useCallback(() => {
    const trimmed = value.trim();
    if (trimmed === '') {
      setEditing(false);
      setError(null);
      onUpdateRate(band.id, null);
      return;
    }
    const validationError = validateRate(trimmed);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setEditing(false);
    onUpdateRate(band.id, normalizeDecimal(trimmed));
  }, [value, band.id, onUpdateRate]);

  function cancel() {
    setValue(band.hourlyRate ?? '');
    setError(null);
    setEditing(false);
  }

  if (editing) {
    return (
      <div className="flex flex-col items-end" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          aria-label={`Hourly rate for band ${band.name}`}
          aria-invalid={error !== null}
          className="w-20 rounded border border-gray-300 px-1.5 py-0.5 text-right text-[12px] font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        {error && <p className="mt-0.5 text-[11px] text-rose-600">{error}</p>}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      className="font-mono text-[12px] tabular-nums text-ms-text-secondary hover:underline"
    >
      {band.hourlyRate === null ? (
        <span className="text-rose-600">— no rate</span>
      ) : (
        band.hourlyRate
      )}
    </button>
  );
}

export interface BandPanelProps {
  bands: SlaPricingBand[];
  armedBandId: number | null;
  isErasing: boolean;
  /** Painted hour count per band this week, computed by the page. */
  hoursPerBand: Record<number, number>;
  onArmBand: (bandId: number) => void;
  onToggleErase: () => void;
  onAddBand: (input: { name: string; abbreviation: string; color: string; hourlyRate: string | null }) => void;
  onUpdateRate: (bandId: number, hourlyRate: string | null) => void;
  onDeleteBand: (bandId: number) => void;
  disabled?: boolean;
}

export function BandPanel({
  bands,
  armedBandId,
  isErasing,
  hoursPerBand,
  onArmBand,
  onToggleErase,
  onAddBand,
  onUpdateRate,
  onDeleteBand,
  disabled = false,
}: BandPanelProps) {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SlaPricingBand | null>(null);

  return (
    <div className="rounded border border-ms-border-light bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-[13px] font-semibold text-ms-text-strong">Price bands</h3>
        <button
          type="button"
          onClick={() => setIsAddOpen(true)}
          disabled={disabled}
          className="text-[12px] font-medium text-ms-accent hover:underline disabled:opacity-50"
        >
          + Add band
        </button>
      </div>

      {bands.length === 0 ? (
        <p className="text-[13px] text-ms-text-secondary">No bands yet. Add one to start painting.</p>
      ) : (
        <ul className="space-y-1">
          {bands.map((band) => {
            const isArmed = armedBandId === band.id && !isErasing;
            const hours = hoursPerBand[band.id] ?? 0;
            return (
              <li key={band.id}>
                <div
                  role="button"
                  tabIndex={0}
                  aria-pressed={isArmed}
                  onClick={() => !disabled && onArmBand(band.id)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                      e.preventDefault();
                      onArmBand(band.id);
                    }
                  }}
                  className={`flex items-center gap-2 rounded px-2 py-1.5 text-[13px] transition-colors ${
                    isArmed ? 'bg-ms-canvas-light ring-1 ring-ms-accent' : 'hover:bg-ms-canvas-light'
                  } ${disabled ? 'opacity-50' : 'cursor-pointer'}`}
                >
                  <span
                    className="inline-block h-4 w-4 flex-shrink-0 rounded"
                    style={{ backgroundColor: band.color }}
                    aria-hidden="true"
                  />
                  <span className="flex-1 truncate font-medium text-ms-text-strong">{band.name}</span>
                  <RateEditor band={band} onUpdateRate={onUpdateRate} />
                  <span className="ml-2 text-[11px] font-mono tabular-nums text-ms-text-secondary">
                    {hours}h
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingDelete(band);
                    }}
                    aria-label={`Delete band ${band.name}`}
                    className="ml-1 text-ms-text-muted hover:text-rose-600"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <label className="mt-3 flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-[13px] hover:bg-ms-canvas-light">
        <input
          type="checkbox"
          checked={isErasing}
          onChange={onToggleErase}
          disabled={disabled}
          className="h-4 w-4"
          aria-label="Erase / clear hours"
        />
        <span className="font-medium text-ms-text-strong">Erase / clear hours</span>
      </label>

      <AddBandModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSubmit={(input) => {
          onAddBand(input);
          setIsAddOpen(false);
        }}
        existingBandCount={bands.length}
      />

      <DeleteBandConfirmModal
        isOpen={pendingDelete !== null}
        band={pendingDelete}
        paintedHours={pendingDelete ? (hoursPerBand[pendingDelete.id] ?? 0) : 0}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) onDeleteBand(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}

export default BandPanel;
